import React from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { UsageMeter } from '../../../ui/usage_meter/UsageMeter';
import { CandiBankStats } from '../../../utils/types/candi_bank_types';
import { creditTone, formatResetDate } from '../../../utils/types/credits_types';

interface BankStatsBarProps {
  stats: CandiBankStats | null;
  loading: boolean;
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ label, value, sub }) => (
  <Paper
    variant='outlined'
    sx={{
      p: 1.5,
      borderRadius: 2,
      bgcolor: '#ffffff',
      borderColor: 'divider',
    }}
  >
    <Typography
      variant='caption'
      fontWeight={700}
      color='text.disabled'
      textTransform='uppercase'
      letterSpacing='0.05em'
      display='block'
    >
      {label}
    </Typography>
    <Typography
      variant='h6'
      fontWeight={800}
      color='text.primary'
      sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
    >
      {value}
    </Typography>
    {sub && (
      <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.25 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const fmt = (n: number) => n.toLocaleString();

/** Headline numbers for the bank, plus this month's CV credit meter. */
export const BankStatsBar: React.FC<BankStatsBarProps> = ({ stats, loading }) => {
  if (loading && !stats) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1.5,
          mb: 3,
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant='rectangular'
            height={74}
            sx={{
              borderRadius: 2,
              gridColumn: i === 3 ? { md: 'span 2' } : undefined,
            }}
          />
        ))}
      </Box>
    );
  }
  if (!stats) return null;

  const { credits } = stats;
  const tone = creditTone(credits);
  const monthName = new Date(credits.resetsAt * 1000 - 1).toLocaleDateString('en-GB', {
    month: 'long',
    timeZone: 'UTC',
  });

  const getCreditTileStyles = () => {
    if (tone === 'crit') {
      return { bgcolor: '#fff1f2', borderColor: '#fecdd3' };
    }
    if (tone === 'warn') {
      return { bgcolor: '#fffbeb', borderColor: '#fde68a' };
    }
    return { bgcolor: '#f0fdf4', borderColor: '#bbf7d0' };
  };

  const creditStyles = getCreditTileStyles();

  return (
    <Box
      data-testid='bank-stats-bar'
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
        gap: 1.5,
        mb: 3,
      }}
    >
      <Tile label='Candidates' value={fmt(stats.totalCandidates)} sub='distinct people' />
      <Tile
        label='CVs in bank'
        value={fmt(stats.totalCvs)}
        sub={`${fmt(stats.bankUploads)} from uploads`}
      />
      <Tile
        label='Processing'
        value={fmt(stats.processing)}
        sub={
          stats.failed > 0 ? (
            <Typography component='span' variant='caption' color='error.main' fontWeight={600}>
              {fmt(stats.failed)} failed
            </Typography>
          ) : (
            'all parsed'
          )
        }
      />
      <Paper
        variant='outlined'
        sx={{
          gridColumn: { xs: 'span 2', md: 'span 2' },
          p: 1.5,
          borderRadius: 2,
          ...creditStyles,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant='caption'
            fontWeight={700}
            color='text.secondary'
            textTransform='uppercase'
            letterSpacing='0.05em'
          >
            CV credits · {monthName}
          </Typography>
          {tone === 'crit' && (
            <Typography
              variant='caption'
              fontWeight={800}
              color='error.dark'
              textTransform='uppercase'
              letterSpacing='0.05em'
            >
              Limit reached
            </Typography>
          )}
        </Box>
        {credits.unlimited ? (
          <>
            <Typography variant='h6' fontWeight={800} color='text.primary' sx={{ mt: 0.25 }}>
              Unlimited
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ mt: 0.25 }}>
              {fmt(credits.used)} uploaded this month
            </Typography>
          </>
        ) : (
          <>
            <Typography
              variant='h6'
              fontWeight={800}
              color='text.primary'
              sx={{ mt: 0.25, fontVariantNumeric: 'tabular-nums' }}
            >
              {fmt(credits.remaining ?? 0)}
              <Typography
                component='span'
                variant='body2'
                fontWeight={600}
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                of {fmt(credits.monthlyLimit ?? 0)} left
              </Typography>
            </Typography>
            <UsageMeter
              used={credits.used}
              limit={credits.monthlyLimit}
              tone={tone}
              sx={{ mt: 1 }}
              leftLabel={
                tone === 'crit'
                  ? 'Monthly limit reached — contact your admin'
                  : `${fmt(credits.used)} used`
              }
              rightLabel={`resets ${formatResetDate(credits)}`}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};
